import { create } from "zustand";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { ProjectApi } from "../api/projectApi";
import { useProjectStore } from "../core_stores/useProjectStore";

interface ProjectConfigActions {
  initialize: () => Promise<void>;
  loadData: () => Promise<void>;
  dispose: () => void;
}

interface ProjectConfigState {
  name: string | null;
  packageName: string | null;

  actions: ProjectConfigActions;
}

const useProjectConfigStore = create<ProjectConfigState>()((set) => {
  let unsubscribeLatestMsg: (() => void) | null = null;
  let unsubscribeHistory: (() => void) | null = null;

  async function loadProjectConfig(
    fallbackConfig?: SharedMessageTypes.ProjectConfig
  ) {
    const projectSettings = await ProjectApi.loadProjectSettings(
      useProjectStore.getState().projectId!
    );
    const projectName =
      projectSettings["app_name"] ?? fallbackConfig?.projectName;
    const packageName =
      projectSettings["package_name"] ?? fallbackConfig?.packageName;

    set({
      name: projectName,
      packageName: packageName,
    });
  }

  return {
    name: null,
    packageName: null,

    actions: {
      initialize: async () => {
        const projectId = useProjectStore.getState().projectId;
        if (projectId) {
          await loadProjectConfig();
        }
        unsubscribeLatestMsg = useProjectStore.subscribe(
          (state) => state.latestMsg,
          (latestMsg) => {
            if (
              latestMsg &&
              latestMsg.type ===
                SharedMessageTypes.MessageTypeEnum.Enum.projectConfig
            ) {
              loadProjectConfig(latestMsg.content);
            }
          }
        );
        unsubscribeHistory = useProjectStore.subscribe(
          (state) => state.history,
          (history) => {
            const latestProjectConfig = [...(history ?? [])]
              .reverse()
              .find(
                (msg) =>
                  msg.type ===
                  SharedMessageTypes.MessageTypeEnum.Enum.projectConfig
              );
            const projectConfigMessage = latestProjectConfig
              ? latestProjectConfig.content
              : undefined;
            loadProjectConfig(projectConfigMessage);
          }
        );
      },

      loadData: async () => {
        const projectId = useProjectStore.getState().projectId;
        if (!projectId) {
          return;
        }
        const response = await ProjectApi.loadProjectSettings(projectId);
        set({
          name: response.app_name,
          packageName: response.package_name,
        });
      },

      dispose: () => {
        if (unsubscribeLatestMsg) {
          unsubscribeLatestMsg();
          unsubscribeLatestMsg = null;
        }
        if (unsubscribeHistory) {
          unsubscribeHistory();
          unsubscribeHistory = null;
        }
        set({
          name: null,
          packageName: null,
        });
      },
    },
  };
});

export const useProjectName = () =>
  useProjectConfigStore((state) => state.name);
export const useProjectPackageName = () =>
  useProjectConfigStore((state) => state.packageName);
export const useProjectConfigActions = () =>
  useProjectConfigStore((state) => state.actions);
